import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { AnimatedIcon, IconName } from './icons';

export type ToastType = 'success' | 'error' | 'info' | 'warning';

interface Toast {
  id: string;
  type: ToastType;
  message: string;
  duration?: number;
}

interface ToastContextValue {
  toasts: Toast[];
  showToast: (message: string, type?: ToastType, duration?: number) => void;
  dismissToast: (id: string) => void;
}

const ToastContext = createContext<ToastContextValue>({
  toasts: [],
  showToast: () => {},
  dismissToast: () => {},
});

export const useToast = () => useContext(ToastContext);

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const counterRef = useRef(0);

  const dismissToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const showToast = useCallback((message: string, type: ToastType = 'info', duration = 4000) => {
    const id = `toast-${++counterRef.current}`;
    const toast: Toast = { id, type, message, duration };
    setToasts((prev) => [...prev.slice(-4), toast]); // max 5 toasts
    if (duration > 0) {
      setTimeout(() => dismissToast(id), duration);
    }
  }, [dismissToast]);

  return (
    <ToastContext.Provider value={{ toasts, showToast, dismissToast }}>
      {children}
      <ToastContainer toasts={toasts} onDismiss={dismissToast} />
    </ToastContext.Provider>
  );
};

const TOAST_COLORS: Record<ToastType, { bg: string; border: string; color: string; icon: IconName }> = {
  success: { bg: 'var(--sc-success-bg, #ECFDF5)', border: 'var(--sc-success, #10B981)', color: 'var(--sc-success-text, #0D7A6F)', icon: 'success' },
  error:   { bg: 'var(--sc-error-bg, #FEE2E2)', border: 'var(--sc-error, #EF4444)', color: 'var(--sc-error-text, #B91C1C)', icon: 'error' },
  warning: { bg: 'var(--sc-warning-bg, #FEF9C3)', border: 'var(--sc-warning, #F59E0B)', color: 'var(--sc-warning-text, #92400E)', icon: 'warning' },
  info:    { bg: 'var(--sc-info-bg, #DBEAFE)', border: 'var(--sc-info, #3B82F6)', color: 'var(--sc-info-text, #1E40AF)', icon: 'info' },
};

const ToastContainer: React.FC<{ toasts: Toast[]; onDismiss: (id: string) => void }> = ({ toasts, onDismiss }) => {
  if (toasts.length === 0) return null;
  return (
    <div style={{
      position: 'fixed',
      top: 16,
      left: '50%',
      transform: 'translateX(-50%)',
      width: 'calc(100% - 32px)',
      maxWidth: 400,
      zIndex: 9999,
      display: 'flex',
      flexDirection: 'column',
      gap: 10,
      pointerEvents: 'none',
    }}>
      {toasts.map((t) => {
        const c = TOAST_COLORS[t.type];
        return (
          <div key={t.id} 
            className="sc-animate-toast-in"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              background: c.bg,
              border: `1px solid ${c.border}`,
              borderRadius: 14,
              padding: '14px 18px',
              boxShadow: 'var(--sc-shadow-lg, 0 10px 30px -8px rgba(15, 23, 42, 0.08))',
              pointerEvents: 'auto',
            }}
          >
            <AnimatedIcon name={c.icon} size={20} />
            <span style={{ flex: 1, fontSize: 13, fontWeight: 600, color: c.color }}>{t.message}</span>
            <button
              onClick={() => onDismiss(t.id)}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: c.color,
                fontSize: 18,
                padding: '0 4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                lineHeight: 1,
              }}
            >×</button>
          </div>
        );
      })}
    </div>
  );
};
